import { disableVoicePushDevice, saveVoicePushDevice } from '../api/communicationApi';

const PREFIX = 'voice-reception:';
const OWNER_KEY = 'voice-push-owner';
const WORKER_PATH = '/voice-reception-sw.js';
const UUID_PATTERN = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
let workerPromise;
const volatile = new Map();
const readSetting = (key) => {
  if (volatile.has(key)) return volatile.get(key);
  try { return localStorage.getItem(key); } catch (_) { return null; }
};
const writeSetting = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
    volatile.delete(key);
  } catch (_) {
    if (volatile.size >= 32) volatile.delete(volatile.keys().next().value);
    volatile.set(key, value);
  }
};

export const isVoiceReference = (value) => typeof value === 'string' && UUID_PATTERN.test(value);
export const getVoicePreferences = (userId) => {
  let saved = {};
  try { saved = JSON.parse(readSetting(PREFIX + userId) || '{}'); } catch (_) { /* default */ }
  const result = {
    enabled: saved.enabled !== false,
    push: saved.push === true,
    deviceId: isVoiceReference(saved.deviceId) ? saved.deviceId : window.crypto.randomUUID(),
    epoch: isVoiceReference(saved.epoch) ? saved.epoch : window.crypto.randomUUID(),
  };
  if (!isVoiceReference(saved.deviceId) || !isVoiceReference(saved.epoch)) {
    writeSetting(PREFIX + userId, JSON.stringify(result));
  }
  return result;
};
export const saveVoicePreferences = (userId, preferences) => {
  writeSetting(PREFIX + userId, JSON.stringify(preferences));
  window.dispatchEvent(new CustomEvent('voice-preferences-changed', { detail: { userId } }));
};
export const voiceCapabilities = () => {
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  return {
    secure: window.isSecureContext,
    notifications: 'Notification' in window,
    serviceWorker: 'serviceWorker' in navigator,
    push: 'PushManager' in window,
    microphone: !!navigator.mediaDevices?.getUserMedia,
    standalone, needsHomeScreen: ios && !standalone,
  };
};
export const notificationPermission = () => 'Notification' in window ? Notification.permission : 'unsupported';

export const getVoiceWorker = async () => {
  if (!window.isSecureContext || !('serviceWorker' in navigator)) {
    throw new Error('Benachrichtigungen benötigen einen unterstützten Browser und HTTPS.');
  }
  if (!workerPromise) {
    workerPromise = (async () => {
      const existing = await navigator.serviceWorker.getRegistration('/');
      const current = existing?.active || existing?.waiting || existing?.installing;
      if (current && new URL(current.scriptURL).pathname !== WORKER_PATH) {
        throw new Error('Benachrichtigungen benötigen die Einrichtung durch die ERP-Administration.');
      }
      const registration = await navigator.serviceWorker.register(WORKER_PATH, { scope: '/', updateViaCache: 'none' });
      // ready can otherwise wait forever when service-worker activation is blocked.
      let activationTimeout;
      try {
        const ready = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) => { activationTimeout = setTimeout(() => reject(new Error('Benachrichtigungen konnten nicht gestartet werden.')), 8000); }),
        ]);
        if (new URL(ready.active.scriptURL).pathname !== WORKER_PATH) throw new Error('ERP-Benachrichtigungen bitte erneut einrichten.');
        return registration;
      } finally { clearTimeout(activationTimeout); }
    })().catch((error) => { workerPromise = null; throw error; });
  }
  return workerPromise;
};

const workerMessage = async (message) => {
  const registration = await getVoiceWorker();
  await new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timeout = setTimeout(() => { channel.port1.close(); reject(new Error('Geräteeinstellung konnte nicht bestätigt werden.')); }, 3000);
    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      channel.port1.close();
      if (event.data?.ok) resolve(); else reject(new Error('Geräteeinstellung konnte nicht gespeichert werden.'));
    };
    registration.active.postMessage(message, [channel.port2]);
  });
};

export const closeCallNotifications = async (reference) => {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration || new URL(registration.active?.scriptURL || window.location.href).pathname !== WORKER_PATH) return;
  const notifications = await registration.getNotifications();
  notifications.filter((n) => n.tag?.startsWith('voice-call-')
    && (!reference || n.tag === `voice-call-${reference}`)).forEach((n) => n.close());
};

export const showCallNotification = async (reference, test = false) => {
  if (notificationPermission() !== 'granted') return false;
  const registration = await getVoiceWorker();
  await registration.showNotification(test ? 'ERP-Testhinweis' : 'Eingehender Firmenanruf', {
    body: test ? 'Benachrichtigungen funktionieren auf diesem Gerät.' : 'ERP öffnen, um den Anruf zu prüfen.',
    icon: '/favicon.png', badge: '/favicon.png',
    tag: test ? 'voice-test' : `voice-call-${reference || 'current'}`,
    data: { path: '/communication' },
    ...(test ? {} : { requireInteraction: true }),
  });
  return true;
};

const applicationKey = (value) => {
  const text = atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4));
  return Uint8Array.from(text, (character) => character.charCodeAt(0));
};

export const clearVoicePush = async (userId, preferences, { unsubscribe = true } = {}) => {
  const failures = [];
  const owner = readSetting(OWNER_KEY);
  const ownsBrowser = !owner || owner === userId;
  // Disable local delivery before network calls. This protects logout even when offline.
  if ('serviceWorker' in navigator) {
    try { await workerMessage({ type: 'voice-device-disabled', epoch: preferences?.epoch }); } catch (_) { failures.push('worker'); }
    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      const ours = registration && new URL(registration.active?.scriptURL || window.location.href).pathname === WORKER_PATH;
      const subscription = ours ? await registration.pushManager?.getSubscription() : null;
      if (unsubscribe && subscription && ownsBrowser) await subscription.unsubscribe();
      if (ownsBrowser) await closeCallNotifications();
    } catch (_) { failures.push('browser'); }
  }
  if (preferences?.deviceId) {
    try { await disableVoicePushDevice(preferences.deviceId); } catch (_) { failures.push('server'); }
  }
  if (readSetting(OWNER_KEY) === userId) writeSetting(OWNER_KEY, null);
  return failures;
};

export const revokeVoicePushOnLogout = async (userId) => {
  if (!userId) return;
  if (!readSetting(PREFIX + userId)) {
    await closeCallNotifications().catch(() => {});
    return;
  }
  const preferences = getVoicePreferences(userId);
  saveVoicePreferences(userId, { ...preferences, push: false, epoch: window.crypto.randomUUID() });
  await clearVoicePush(userId, preferences);
};

export const synchronizeVoicePush = async (userId, preferences, config) => {
  if (!preferences.enabled || !preferences.push || !config?.enabled || !voiceSessionMatches(userId)) return null;
  if (notificationPermission() !== 'granted') throw new Error('Benachrichtigungen bitte in den Geräteeinstellungen erlauben.');
  await isolateVoicePushOwner(userId);
  const registration = await getVoiceWorker();
  let subscription = await registration.pushManager.getSubscription();
  const key = applicationKey(config.public_key);
  if (subscription?.options?.applicationServerKey) {
    const oldKey = new Uint8Array(subscription.options.applicationServerKey);
    if (oldKey.length !== key.length || oldKey.some((value, index) => value !== key[index])) {
      await subscription.unsubscribe();
      subscription = null;
    }
  }
  subscription ||= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const result = await saveVoicePushDevice({
    device_id: preferences.deviceId, client_epoch: preferences.epoch, subscription: subscription.toJSON(),
  });
  // A logout/toggle that happened during the HTTP request must win.
  const latest = getVoicePreferences(userId);
  if (!latest.push || !latest.enabled || latest.epoch !== preferences.epoch || !voiceSessionMatches(userId)) {
    await clearVoicePush(userId, preferences);
    return null;
  }
  await workerMessage({ type: 'voice-device-enabled', deviceId: preferences.deviceId,
    epoch: preferences.epoch, expiresAt: result.expires_at * 1000 });
  const confirmed = getVoicePreferences(userId);
  if (!confirmed.push || !confirmed.enabled || confirmed.epoch !== preferences.epoch || !voiceSessionMatches(userId)) {
    await clearVoicePush(userId, preferences);
    return null;
  }
  writeSetting(OWNER_KEY, userId);
  return result;
};

export const isolateVoicePushOwner = async (userId) => {
  const previousOwner = readSetting(OWNER_KEY);
  if (previousOwner && previousOwner !== userId) {
    const previous = getVoicePreferences(previousOwner);
    saveVoicePreferences(previousOwner, { ...previous, push: false, epoch: window.crypto.randomUUID() });
    await clearVoicePush(previousOwner, previous);
  }
};

export const voiceSessionMatches = (userId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const raw = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(raw + '='.repeat((4 - raw.length % 4) % 4)));
    // This is only a local fail-closed UI guard. The server verifies signatures.
    return claims.sub === userId && typeof claims.exp === 'number' && claims.exp * 1000 > Date.now();
  } catch (_) { return false; }
};
